import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminUserService, ProfileApiResponse } from '@/lib/api/services/admin-user'
import * as authService from '@/lib/api/services/auth'
import { authKeys } from '@/lib/api/hooks/auth'
import type {
  ApiResponse,
  AdminUser,
  UpdateAdminUserRequest
} from '@/lib/api/types'
import { showToast } from '@/lib/toast'

// Query keys
export const adminUserKeys = {
  all: ['admin-users'] as const,
  lists: () => [...adminUserKeys.all, 'list'] as const,
  list: () => [...adminUserKeys.lists()] as const
}

// Hook to fetch all admin users
export function useAdminUsers() {
  return useQuery<ApiResponse<AdminUser[]>, Error>({
    queryKey: adminUserKeys.list(),
    queryFn: () => adminUserService.getAllAdminUsers()
  })
}

// Hook to fetch current user profile (real-time data from API)
export function useCurrentUserProfile() {
  return useQuery<ApiResponse<ProfileApiResponse[]>, Error>({
    queryKey: adminUserKeys.list(),
    queryFn: () => adminUserService.getCurrentUserProfile()
  })
}

// Hook to update admin user
export function useUpdateAdminUser() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<AdminUser>,
    Error,
    UpdateAdminUserRequest
  >({
    mutationFn: (data: UpdateAdminUserRequest) =>
      adminUserService.updateAdminUser(data),
    onSuccess: (response) => {
      // Invalidate admin users list
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      
      // CRITICAL: Invalidate current user query to fetch fresh data from API
      // This ensures the profile page shows updated data immediately
      queryClient.invalidateQueries({ queryKey: authKeys.user() })
      
      // If the updated user is the current user, also update sessionStorage immediately
      const currentUser = authService.getCurrentUser()
      if (response.data && currentUser && response.data.userProfile.userId === currentUser.userProfile.userId) {
        // Update sessionStorage and module cache
        authService.updateCurrentUser(response.data)
        
        // Update React Query cache directly for immediate UI update
        queryClient.setQueryData(authKeys.user(), response.data)
      }
      
      showToast.success('Admin user updated successfully')
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to update admin user')
    }
  })
}

// Hook to toggle account status (block/unblock)
export function useToggleAccountStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<AdminUser>,
    Error,
    { userId: string; blockStatus: boolean }
  >({
    mutationFn: ({ userId, blockStatus }) =>
      adminUserService.toggleAccountStatus(userId, blockStatus),
    onSuccess: (_data, { blockStatus }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      showToast.success(
        blockStatus ? 'User blocked successfully' : 'User unblocked successfully'
      )
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to update account status')
    }
  })
}

// Hook to delete admin user
export function useDeleteAdminUser() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<void>,
    Error,
    { userId: string; deleteStatus: boolean }
  >({
    mutationFn: ({ userId, deleteStatus }) =>
      adminUserService.deleteAdminUser(userId, deleteStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      showToast.success('Admin user deleted successfully')
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to delete admin user')
    }
  })
}

// Hook to change current admin password
export function useChangePassword() {
  return useMutation<
    ApiResponse<null>,
    Error,
    { oldPassword: string; newPassword: string }
  >({
    mutationFn: (data) => adminUserService.changePassword(data),
    onSuccess: () => {
      showToast.success('Password changed successfully')
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to change password')
    }
  })
}

